import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import { BootLoading, Forbidden } from "../ui/Feedback.jsx";

/**
 * Gate for every authenticated route. Redirects to /login and remembers where
 * the user was headed.
 */
export function RequireAuth() {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === "loading") return <BootLoading />;
  // The query string is carried across the redirect: a failed federated sign-in
  // lands on the landing page with `?error=<code>`, and dropping it here is what
  // left the login screen saying only "not signed in".
  if (!user) return <Navigate to={`/login${location.search}`} replace state={{ from: location }} />;
  return <Outlet />;
}

/** Public screens (/login, /signup, …) bounce to the dashboard once signed in. */
export function PublicOnly() {
  const { status, user } = useAuth();

  if (status === "loading") return <BootLoading />;
  if (user) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * DESIGN.md is explicit that AdminPanel itself does not check permissions —
 * the route must. A non-admin sees a 403 state, not the ops panel.
 *
 * The gate reads `can_admin`, not `role`: an administrator who has not enrolled
 * a second factor has the role but not the capability, and the admin API refuses
 * them too. Showing the forbidden state (rather than mounting a panel that
 * fails every call) is what the account screen's enrolment banner replaces.
 */
export function RequireAdmin() {
  const { status, user } = useAuth();

  if (status === "loading") return <BootLoading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.can_admin) return <Forbidden />;
  return <Outlet />;
}
